import type { OpenAPIResponse, OpenAPIVoidResponse, PhotoAPI } from '../../api/api-switcher'
import { toApiResultAsync, type ApiResult } from '../../api/api-error'
import { mergePhotos, type PartialPhoto, updatePhotos } from '../../components/PhotoList/findLoadTop'
import type { GetPhoto, Photo, ReactionToPhoto, Tags, TagsToPhoto } from './types'

type ListAllPhotosDate = (desc: boolean, signal: AbortSignal)=> ReturnType<typeof PhotoAPI.listAllPhotosDate>
type ListPhotos = (desc: boolean, offset: Date, signal?: AbortSignal)=> ReturnType<typeof PhotoAPI.listPhotos>

export type UnlimitedCacheProps<T> = {
  getDirectory: (signal?: AbortSignal)=> OpenAPIResponse<T>
  getPhoto: GetPhoto
  tagsToPhoto: TagsToPhoto
  reactionToPhoto: ReactionToPhoto
  listPhotos: ListPhotos
  listAllPhotosDate: ListAllPhotosDate
  removePhotos: (ids: string[]) => OpenAPIVoidResponse
}
type PhotoResource = {
  // Reactのuse()へ毎回同じPromiseを渡しつつ、更新APIから表示中の写真も変更できるよう
  // Promiseとその解決値への参照を同じリソースとして管理する。
  promise: Promise<ApiResult<Photo>>
  photo?: Photo
}
/**
 * キャッシュを返すのはlistAllPhotosの戻り値だけ
 * listとphotoの移動時のロードを減らしたい & あくまでもAPIのラッパーとして振る舞うため
 */
export class UnlimitedPartialPhotosCache<T> {
  #directory_promise: Promise<ApiResult<T>> | undefined
  readonly #getDirectory: (signal?: AbortSignal)=> OpenAPIResponse<T>
  #cache_photos: PartialPhoto<'added'>[] | undefined
  readonly #photo_resources = new Map<string, PhotoResource>()
  readonly #getPhoto: GetPhoto
  readonly #tagsToPhoto: TagsToPhoto
  readonly #reactionToPhoto: ReactionToPhoto
  readonly #listPhotos: ListPhotos
  readonly #listAllPhotosDate: ListAllPhotosDate
  readonly #removePhotos: (ids: string[]) => OpenAPIVoidResponse
  constructor ({
    listPhotos, listAllPhotosDate, removePhotos, getDirectory, getPhoto, tagsToPhoto, reactionToPhoto
  }: UnlimitedCacheProps<T>) {
    this.#getPhoto = getPhoto
    this.#tagsToPhoto = tagsToPhoto
    this.#reactionToPhoto = reactionToPhoto
    this.#listPhotos = listPhotos
    this.#listAllPhotosDate = listAllPhotosDate
    this.#removePhotos = removePhotos
    this.#getDirectory = getDirectory
  }
  async listAllPartialPhotos (desc: boolean, signal: AbortSignal): OpenAPIResponse<PartialPhoto<'added'>[]> {
    if (!this.#cache_photos) {
      const photos = await this.#listAllPhotosDate(true, signal)
      if (photos.error) return photos
      this.#cache_photos = photos.data.map(d=> ({added: new Date(d), loading_key: 0}))
    }
    return {
      data: desc ? this.#cache_photos : this.#cache_photos.toReversed(),
      error: undefined,
      response: new Response()
    }
  }
  async listPhotos (desc: boolean, offset: Date, signal?: AbortSignal): OpenAPIResponse<Photo[]> {
    const photos = await this.#listPhotos(desc, offset, signal)
    const dummy_key = 1
    if (photos.data) this.#cache_photos = updatePhotos('added', dummy_key, this.#cache_photos!, photos.data)
    return photos
  }
  getPhoto (id: string): Promise<ApiResult<Photo>> {
    const cached = this.#photo_resources.get(id)
    if (cached) return cached.promise

    // 一覧取得済みなら同じ写真オブジェクトを使い、詳細APIの重複呼び出しを避ける。
    const cache_photo = this.#cache_photos?.find((p): p is Photo => 'photo_id' in p && p.photo_id === id)
    const fetch_promise = cache_photo
      ? Promise.resolve({data: cache_photo, error: undefined, response: new Response()} as ApiResult<Photo>)
      : toApiResultAsync(() => this.#getPhoto(id))
    const resource: PhotoResource = {promise: fetch_promise}
    resource.promise = fetch_promise.then(result => {
      // 削除・reset後に古いPromiseが完了しても、破棄済みリソースを復活させない。
      if (this.#photo_resources.get(id) === resource && result.data) resource.photo = result.data
      return result
    })
    this.#photo_resources.set(id, resource)
    return resource.promise
  }
  async tagsToPhoto (id: string, tags: Tags): OpenAPIResponse<Omit<Photo, 'added'>> {
    const {data: new_photo, error, response} = await this.#tagsToPhoto(id, tags)
    if (error) return {data: undefined, error, response}
    if (this.#cache_photos) {
      const photo = this.#cache_photos.find(p=> 'photo_id' in p && p.photo_id === id)
      if (photo) Object.assign(photo, new_photo)
    }
    // 一覧と写真詳細が別オブジェクトの場合も、表示中リソースへ同じ更新を反映する。
    const resource_photo = this.#photo_resources.get(id)?.photo
    if (resource_photo) Object.assign(resource_photo, new_photo)
    return {data: new_photo, error: undefined, response}
  }
  async reactionToPhoto (id: string, reaction: string, add: boolean): OpenAPIResponse<Photo> {
    const {data: new_photo, error, response} = await this.#reactionToPhoto(id, reaction, add)
    if (error) return {data: undefined, error, response}
    if (this.#cache_photos) {
      const photo = this.#cache_photos.find(p=> 'photo_id' in p && p.photo_id === id)
      if (photo) {
        // Only update the reactions field since that's what the API returns
        if ('reactions' in new_photo && !('loading_key' in photo)) {
          (photo as Photo).reactions = new_photo.reactions
        } else {
          Object.assign(photo, new_photo)
        }
      }
    }
    // Viewerが写真詳細リソースを読んでいる場合も、同じオブジェクトへ更新を反映する。
    const resource_photo = this.#photo_resources.get(id)?.photo
    if (resource_photo) {
      if ('reactions' in new_photo && !('loading_key' in resource_photo)) {
        resource_photo.reactions = new_photo.reactions
      } else {
        Object.assign(resource_photo, new_photo)
      }
    }
    return {data: new_photo, error: undefined, response}
  }
  async removePhotos (ids: string[], only_cache = false): OpenAPIVoidResponse {
    // DMの写真の場合、idsはphoto_idではなくPhotoAddedAtのタイムスタンプになる
    // ISO文字列に変換された'added'フィールドとマッチする必要がある
    this.#cache_photos = this.#cache_photos!.filter(p=> {
      if ('photo_id' in p) {
        if (ids.includes(p.photo_id)) return false
        const photoAddedAt = p.added.toISOString()
        return !ids.includes(photoAddedAt)
      }
      return true // 写真以外のエントリは保持する
    })
    // use()が読んでいる写真リソースも同じ基準（photo_id / DMのadded ISO）で破棄する。
    // ISO一致は解決済みリソースのadded由来で判定する。DM削除は表示中（=解決済み）の
    // 写真のaddedを渡すため、未解決リソースをISOで消せない穴は実経路では踏まない。
    for (const [key, resource] of this.#photo_resources) {
      const added_iso = resource.photo?.added.toISOString()
      if (ids.includes(key) || (added_iso !== undefined && ids.includes(added_iso))) {
        this.#photo_resources.delete(key)
      }
    }
    if (!only_cache) return this.#removePhotos(ids)
    return {response: new Response()}
  }
  getDirectory (): Promise<ApiResult<T>> {
    this.#directory_promise ??= toApiResultAsync(() => this.#getDirectory())
    return this.#directory_promise
  }
  updateDirectory (directory: T) {
    this.#directory_promise = Promise.resolve({
      data: directory,
      error: undefined,
      response: new Response(),
    })
  }
  mergePhotos (desc: boolean, photos: Photo[]) {
    this.#cache_photos = mergePhotos('added', desc, this.#cache_photos || [], photos)
    return this.#cache_photos
  }
  reset () {
    this.#cache_photos = undefined
    this.#directory_promise = undefined
    this.#photo_resources.clear()
  }
}
